import React, { useEffect, useState } from "react";
import {
  Card,
  Switch,
  Button,
  Typography,
  Space,
  Tag,
  message,
  Divider,
  Alert,
  Spin,
} from "antd";
import { ArrowRightOutlined, LockOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import axiosInstance from "../../../services/apis";
import { fetchDeliveryDashboard } from "../deliveryThunks";
import { selectAuthInfo } from "../../Auth/authSelectors";

const { Text, Title } = Typography;

const PICKUP_ALL_STEPS = [
  { key: "picked", label: "Picked", required: true, color: "processing", description: "Item picked up from customer" },
  { key: "in_transit", label: "In Transit", required: false, color: "warning", description: "Item in transit to warehouse" },
  { key: "delivered", label: "Delivered", required: false, color: "blue", description: "Item delivered to destination" },
  { key: "verify", label: "Verify", required: false, color: "purple", description: "Verification of picked items" },
  { key: "tatmeen", label: "Tatmeen", required: false, color: "geekblue", description: "GS1/Tatmeen barcode verification" },
  { key: "restored", label: "Restored", required: true, color: "success", description: "Item restored to stock" },
];

const ALL_STEPS = [
  { key: "picking", label: "Picking", required: true, description: "Item picked from warehouse" },
  { key: "picking_verification", label: "Picking Verification", required: true, description: "Verification of picked items before packing" },
  { key: "tatmeen_verification", label: "Tatmeen Verification", required: false, description: "GS1/Tatmeen barcode scan verification" },
  { key: "packing", label: "Packing", required: true, description: "Item packed for delivery" },
  { key: "on_transit", label: "On Transit", required: false, description: "Item dispatched for delivery" },
  { key: "delivered", label: "Delivered", required: false, description: "Item delivered to customer" },
  { key: "completed", label: "Completed", required: true, description: "Delivery confirmed and closed" },
];

const DeliveryStatusFlow = () => {
  const dispatch = useDispatch();
  const authInfo = useSelector(selectAuthInfo);

  // ── Tatmeen (GS1 barcode) verification only applies to UAE Drug Store companies ──
  const isDrugStoreUAE =
    authInfo?.company_type === "Drug Store" &&
    authInfo?.country === "United Arab Emirates";
  const VISIBLE_STEPS = isDrugStoreUAE
    ? ALL_STEPS
    : ALL_STEPS.filter((s) => s.key !== "tatmeen_verification");
  const VISIBLE_PICKUP_STEPS = isDrugStoreUAE
    ? PICKUP_ALL_STEPS
    : PICKUP_ALL_STEPS.filter((s) => s.key !== "tatmeen");

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [pickupConfig, setPickupConfig] = useState(null);
  const [pickupLoading, setPickupLoading] = useState(true);
  const [pickupSaving, setPickupSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
    fetchPickupConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/delivery/status-flow-config");
      const data = res.data?.data || {};
      const enabledSet = new Set(data.enabled_steps || VISIBLE_STEPS.map((s) => s.key));
      setConfig(
        VISIBLE_STEPS.map((step) => ({
          ...step,
          enabled: step.required || enabledSet.has(step.key),
        }))
      );
    } catch {
      // Default: all enabled
      setConfig(VISIBLE_STEPS.map((s) => ({ ...s, enabled: true })));
    } finally {
      setLoading(false);
    }
  };

  const fetchPickupConfig = async () => {
    try {
      setPickupLoading(true);
      const res = await axiosInstance.get("/delivery/pickup-status-flow-config");
      const data = res.data?.data || {};
      const enabledSet = new Set(data.enabled_steps || VISIBLE_PICKUP_STEPS.map((s) => s.key));
      setPickupConfig(
        VISIBLE_PICKUP_STEPS.map((step) => ({
          ...step,
          enabled: step.required || enabledSet.has(step.key),
        }))
      );
    } catch {
      setPickupConfig(VISIBLE_PICKUP_STEPS.map((s) => ({ ...s, enabled: true })));
    } finally {
      setPickupLoading(false);
    }
  };

  const toggleStep = (key) => {
    setConfig((prev) =>
      prev.map((s) => (s.key === key && !s.required ? { ...s, enabled: !s.enabled } : s))
    );
  };

  const togglePickupStep = (key) => {
    setPickupConfig((prev) =>
      prev.map((s) => (s.key === key && !s.required ? { ...s, enabled: !s.enabled } : s))
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const enabled_steps = config.filter((s) => s.enabled).map((s) => s.key);
      await axiosInstance.post("/delivery/status-flow-config", { enabled_steps });
      message.success("Status flow configuration saved");
      // Refresh the dashboard's Redux state immediately so the Delivery
      // Pipeline reflects the change without needing a page reload.
      dispatch(fetchDeliveryDashboard({}));
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const handlePickupSave = async () => {
    try {
      setPickupSaving(true);
      const enabled_steps = pickupConfig.filter((s) => s.enabled).map((s) => s.key);
      await axiosInstance.post("/delivery/pickup-status-flow-config", { enabled_steps });
      message.success("Pickup status flow configuration saved");
      // Refresh the dashboard's Redux state immediately so the Pickup
      // Pipeline reflects the change without needing a page reload.
      dispatch(fetchDeliveryDashboard({}));
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to save pickup configuration");
    } finally {
      setPickupSaving(false);
    }
  };

  const enabledSteps = config?.filter((s) => s.enabled) || [];
  const enabledPickupSteps = pickupConfig?.filter((s) => s.enabled) || [];

  if (loading || pickupLoading) return <Spin style={{ display: "block", margin: "40px auto" }} />;

  return (
    <div style={{ maxWidth: 800 }}>
      {/* ── Pickup Status Flow ── */}
      <Card
        title="Pickup Status Flow Configuration"
        style={{ marginBottom: 16 }}
        extra={
          <Button type="primary" loading={pickupSaving} onClick={handlePickupSave}>
            Save Configuration
          </Button>
        }
      >
        <Alert
          type="info"
          showIcon
          message="Configure which steps are active in the pickup request workflow. Required steps cannot be disabled."
          style={{ marginBottom: 20 }}
        />

        <Title level={5} style={{ marginBottom: 12 }}>
          Status Steps
        </Title>

        {pickupConfig?.map((step) => (
          <div
            key={step.key}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 16px",
              marginBottom: 8,
              background: step.enabled ? "#f6ffed" : "#fafafa",
              border: `1px solid ${step.enabled ? "#b7eb8f" : "#d9d9d9"}`,
              borderRadius: 8,
            }}
          >
            <Space direction="vertical" size={2}>
              <Space>
                <Text strong>{step.label}</Text>
                {step.required && (
                  <Tag icon={<LockOutlined />} color="blue">
                    Required
                  </Tag>
                )}
                {!step.required && !step.enabled && <Tag color="default">Disabled</Tag>}
              </Space>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {step.description}
              </Text>
            </Space>
            <Switch
              checked={step.enabled}
              disabled={step.required}
              onChange={() => togglePickupStep(step.key)}
            />
          </div>
        ))}

        {enabledPickupSteps.length > 0 && (
          <>
            <Divider />
            <Title level={5} style={{ marginBottom: 12 }}>
              Active Flow Preview
            </Title>
            <Space style={{ lineHeight: "2", overflowX: "auto", maxWidth: "100%", paddingBottom: 4 }}>
              {enabledPickupSteps.map((step, idx) => (
                <React.Fragment key={step.key}>
                  <Tag color={step.required ? step.color : "green"} style={{ fontSize: 13, padding: "4px 10px" }}>
                    {step.label}
                  </Tag>
                  {idx < enabledPickupSteps.length - 1 && (
                    <ArrowRightOutlined style={{ color: "#aaa" }} />
                  )}
                </React.Fragment>
              ))}
            </Space>
            <div style={{ marginTop: 12 }}>
              <Tag color="error" style={{ fontSize: 13, padding: "4px 10px" }}>Cancelled</Tag>
              <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                Can be cancelled from any active status
              </Text>
            </div>
          </>
        )}
      </Card>

      {/* ── Delivery Job Status Flow ── */}
      <Card
        title="Delivery Job Status Flow Configuration"
        extra={
          <Button type="primary" loading={saving} onClick={handleSave}>
            Save Configuration
          </Button>
        }
      >
        <Alert
          type="info"
          showIcon
          message="Configure which steps are active in the delivery workflow. Required steps cannot be disabled."
          style={{ marginBottom: 20 }}
        />

        <Title level={5} style={{ marginBottom: 12 }}>
          Status Steps
        </Title>

        {config?.map((step) => (
          <div
            key={step.key}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 16px",
              marginBottom: 8,
              background: step.enabled ? "#f6ffed" : "#fafafa",
              border: `1px solid ${step.enabled ? "#b7eb8f" : "#d9d9d9"}`,
              borderRadius: 8,
            }}
          >
            <Space direction="vertical" size={2}>
              <Space>
                <Text strong>{step.label}</Text>
                {step.required && (
                  <Tag icon={<LockOutlined />} color="blue">
                    Required
                  </Tag>
                )}
                {!step.required && !step.enabled && <Tag color="default">Disabled</Tag>}
              </Space>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {step.description}
              </Text>
            </Space>
            <Switch
              checked={step.enabled}
              disabled={step.required}
              onChange={() => toggleStep(step.key)}
            />
          </div>
        ))}

        {enabledSteps.length > 0 && (
          <>
            <Divider />
            <Title level={5} style={{ marginBottom: 12 }}>
              Active Flow Preview
            </Title>
            <Space wrap style={{ lineHeight: "2" }}>
              {enabledSteps.map((step, idx) => (
                <React.Fragment key={step.key}>
                  <Tag color={step.required ? "blue" : "green"} style={{ fontSize: 13, padding: "4px 10px" }}>
                    {step.label}
                  </Tag>
                  {idx < enabledSteps.length - 1 && (
                    <ArrowRightOutlined style={{ color: "#aaa" }} />
                  )}
                </React.Fragment>
              ))}
            </Space>
          </>
        )}
      </Card>
    </div>
  );
};

export default DeliveryStatusFlow;
